"use client";

import { cn } from "@/lib/utils";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  step?: string;
  className?: string;
  children: React.ReactNode;
}

export function FeatureCard({
  icon: Icon,
  title,
  step,
  className,
  children,
}: FeatureCardProps) {
  return (
    <Card
      className={cn(
        "h-full overflow-hidden border-border bg-card transition-colors hover:border-primary/30",
        className
      )}
    >
      <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3">
        <div className="flex items-start justify-between">
          <div className="inline-flex rounded-xl bg-primary/10 p-3">
            <Icon className="h-6 w-6 text-primary" />
          </div>
          {step && (
            <span className="font-pixel text-xl text-muted-foreground/30">
              {step}
            </span>
          )}
        </div>
        <h3 className="mt-4 text-xl font-bold">{title}</h3>
      </CardHeader>
      <CardContent className="p-4 pt-0 text-muted-foreground sm:p-6 sm:pt-0">{children}</CardContent>
    </Card>
  );
}

export function AnimatedFeatureCard({
  index,
  ...props
}: FeatureCardProps & { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
    >
      <FeatureCard {...props} />
    </motion.div>
  );
}
